import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import TableSingleInput from 'notebook/structured-tables/single-inputs/TableSingleInput';
import useSingleInputDragHandlers, {
  getStopDragPropagationProps,
} from 'notebook/structured-tables/single-inputs/useSingleInputDragHandlers';
import {TableEditableClass} from 'notebook/tables/core-ui/TableEditable';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {RowModelProvider} from 'notebook/tables/store/contexts/RowModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {useTableVolatileProps} from 'notebook/tables/store/hooks/useTableProps';
import getTestProps from 'test/lib/cypress/getTestProps';

export interface TableSingleInputGroupProps {
  sliceStart: number;
  // exclusive
  sliceEnd: number;
  rowProps: TableRowProps;
  clearMultiSelect: TableEditableClass['clearMultiSelect'];
  columnWidths: Array<number>;
  setColumnWidth: (columnIndex: number, width: number) => void;
  locked?: boolean;
  tableName?: string;
  storeState?: () => void;
  reorderSingleInputs?: (tableName: string, options: ReorderOptions) => void;
}

export function TableSingleInputGroup(props: TableSingleInputGroupProps): JSX.Element {
  const {sliceStart, sliceEnd, rowProps, tableName, storeState, reorderSingleInputs} = props;
  // Only the individual values used here are subscribed to so that unrelated model changes do not
  // re-render the whole group.
  const columnConstraints = useTableVolatileProps((props) => props.table.columnConstraints);
  const {tableModel: viewer} = useTableModel((model) => model.columnHeader.viewer);
  const {tableModel: locked} = useTableModel((model) => model.columnHeader.locked);

  const numSingleInputs = sliceEnd - sliceStart;
  const canReorder = !viewer && !locked;

  const {dragSourceIndex, dragOverIndex, singleInputDragProps, dropZoneDragProps} =
    useSingleInputDragHandlers({
      numSingleInputs,
      canReorder,
      tableName,
      storeState,
      reorderSingleInputs,
    });

  const inputs = [];
  for (let index = 0; index < numSingleInputs; index++) {
    const j = sliceStart + index;
    const dropZoneIndex = numSingleInputs + index;
    const columnKey = columnConstraints[j]?.structuredTableColumn?.provenanceUuid ?? `col-${j}`;

    inputs.push(
      <div
        key={columnKey}
        className="mediocre-tableEditable-singleInput-wrapper"
        {...getTestProps({component: 'SingleInputGroup', element: `wrapper-${j}-${columnKey}`})}
      >
        <TableSingleInput
          j={j}
          rowProps={rowProps}
          columnWidth={props.columnWidths[j]}
          setColumnWidth={props.setColumnWidth}
          locked={props.locked}
          dragIndex={index}
          isDragging={dragSourceIndex === index}
          isDragOver={dragOverIndex === index && dragSourceIndex !== index}
          {...singleInputDragProps}
        />
        {singleInputDragProps.canDrag && dragSourceIndex != null && (
          <div
            className={classNames('mediocre-tableEditable-singleInput-dropZone', {
              'mediocre-tableEditable-singleInput-dropZone--active': dragOverIndex === dropZoneIndex,
            })}
            data-drop-zone-index={dropZoneIndex}
            {...dropZoneDragProps}
            {...getTestProps({component: 'SingleInputGroup', element: `dropZone-${index}`})}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames('mediocre-tableEditable-singleInputGroup', {
        'mediocre-tableEditable-singleInputGroup--dragging': dragSourceIndex != null,
      })}
      onClick={props.clearMultiSelect}
      {...getStopDragPropagationProps()}
      {...getTestProps({component: 'SingleInputGroup', element: 'root'})}
    >
      <RowModelProvider rowIndex={rowProps.i} isSingleInput={true}>
        {inputs}
      </RowModelProvider>
    </div>
  );
}

/**
 * Custom `React.memo` comparator for {@link TableSingleInputGroup}. Returns `true` (skip re-render)
 * only when every input that affects the rendered output is unchanged.
 *
 * `sliceStart`/`sliceEnd` bound the number of single inputs the group renders
 * (`numSingleInputs = sliceEnd - sliceStart`), so they must be compared: converting a column into
 * (or out of) a single input changes `sliceEnd`, and omitting it would keep the group's rendered
 * count stale until an unrelated prop finally changed (e.g. on the next click).
 */
export function singleInputGroupPropsAreEqual(
  prevProps: TableSingleInputGroupProps,
  nextProps: TableSingleInputGroupProps
): boolean {
  return (
    prevProps.sliceStart === nextProps.sliceStart &&
    prevProps.sliceEnd === nextProps.sliceEnd &&
    isEqual(prevProps.rowProps, nextProps.rowProps) &&
    isEqual(prevProps.columnWidths, nextProps.columnWidths) &&
    prevProps.locked === nextProps.locked &&
    prevProps.tableName === nextProps.tableName &&
    prevProps.storeState === nextProps.storeState &&
    prevProps.reorderSingleInputs === nextProps.reorderSingleInputs
  );
}

// This needs to be memoized to prevent it from re-rendering when other rows / cells change
// TODO(BNCH-113857): This can probably be removed once selectors are enabled
export default React.memo(TableSingleInputGroup, singleInputGroupPropsAreEqual);
